import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, Button, message, Avatar, Typography, Space, Divider, Spin } from 'antd';
import { UserOutlined, SaveOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import Layout from '../components/Layout';
import '../styles/global-input-fixes.css';

const { Title, Text } = Typography;

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Perfil carregado do backend (`getProfile`) que reflete a organização e a
  // filial realmente vinculadas ao usuário no banco.
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      try {
        const data = await apiService.users.getProfile();
        if (!cancelled && data) setProfile(data);
      } catch {
        // Sem token JWT válido (ex.: fluxo ERP/código) → usa o user do contexto.
        if (!cancelled) setProfile(null);
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    };
    loadProfile();
    return () => { cancelled = true; };
  }, []);

  // Base para preenchimento do form e exibição: prioriza o perfil do backend,
  // caindo para o `user` do contexto quando aquele não estiver disponível.
  const source = profile || user || {};

  // Preenche os campos apenas UMA vez (no primeiro carregamento). Sem isso, o
  // carregamento assíncrono do `profile` sobrescreveria a digitação do usuário
  // com o valor antigo gravado no banco.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    form.setFieldsValue({
      name: source.name ?? user.name,
      email: source.email ?? user.email,
      codigo: source.codigo ?? user.codigo
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile, form, source.name, source.email, source.codigo]);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      // O backend só aceita `name`/`pictureUrl`; enviamos apenas o nome.
      const result = await updateProfile({ name: values?.name });
      if (result.success) {
        message.success('Perfil atualizado com sucesso!');
      } else {
        message.error(result.error || 'Erro ao atualizar perfil');
      }
    } catch (error) {
      console.error('[Profile] Erro ao atualizar perfil:', error);
      message.error(error?.message || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div style={{
        padding: '24px',
        background: '#f5f5f5',
        minHeight: 'calc(100vh - 64px)'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <Title level={2} style={{ marginBottom: '24px', color: '#1a1a1a' }}>
            Meu Perfil
          </Title>

          <Card
            style={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <Avatar
                size={80}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: '#1890ff',
                  marginBottom: '16px'
                }}
              />
              <Title level={4} style={{ margin: 0 }}>
                {source.name || user?.name}
              </Title>
              <Text type="secondary">
                {source.email || user?.email}
              </Text>
            </div>

            <Divider />

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
            >
              <Form.Item
                label="Nome"
                name="name"
                rules={[
                  { required: true, message: 'Por favor, insira seu nome!' },
                  { min: 2, message: 'Nome deve ter pelo menos 2 caracteres!' }
                ]}
              >
                <Input
                  placeholder="Seu nome completo"
                />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { type: 'email', message: 'Por favor, insira um email válido!' }
                ]}
              >
                <Input
                  placeholder="seu.email@exemplo.com"
                />
              </Form.Item>

              <Form.Item
                label="Código do Funcionário"
                name="codigo"
              >
                <Input
                  placeholder="Código do funcionário"
                  disabled={true}
                />
              </Form.Item>

              <div style={{ marginTop: '24px' }}>
                <Space style={{ width: '100%' }} direction="vertical">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    block
                  >
                    Salvar Alterações
                  </Button>
                </Space>
              </div>
            </Form>

            <Divider />

            <div>
              <Title level={5} style={{ marginBottom: '16px' }}>
                Informações da Organização
              </Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Organização: </Text>
                  <Text>{source?.organization?.name || 'N/A'}</Text>
                </div>
                <div>
                  <Text strong>Filial: </Text>
                  <Text>{source?.assignedBranch?.name || 'N/A'}</Text>
                </div>
                <div>
                  <Text strong>Roles: </Text>
                  <Text>
                    {source?.roles?.map(ur => ur.role.name).join(', ') || 'N/A'}
                  </Text>
                </div>
                {loadingProfile && (
                  <div>
                    <Spin size="small" /> <Text type="secondary">Carregando dados da organização...</Text>
                  </div>
                )}
              </Space>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;